import { ClientEmail as ClientEmailRaw } from '@provision/entity-types';
import {
  gql,
  QueryHookOptions,
  Reference,
  useMutation,
  useQuery,
} from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { QueryVariables, Records } from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';
import { removeNotDefinedProperties } from 'src/utils/objects';
import { META_DATA_QUERY_FIELDS } from './metadata.fragments';

export const CLIENT_EMAIL_FIELDS = gql`
  fragment ClientEmailFields on ClientEmail {
    id
    clientEmailId
    clientId
    value
    partnerId
    labelId
  }
`;

const GET_CLIENT_EMAILS_QUERY = gql`
  query GetClientEmail($params: QueryParams, $skipTotalRecords: Boolean = false)
  @entity(name: "ClientEmail") {
    getClientEmail(queryParams: $params) {
      records {
        ...ClientEmailFields
      }
      metadata {
        ...MetaDataQueryFields
      }
    }
  }
  ${CLIENT_EMAIL_FIELDS}
  ${META_DATA_QUERY_FIELDS}
`;

type GetClientEmailQuery = {
  getClientEmail: Records<ClientEmail>;
};

export type ClientEmail = Pick<ClientEmailRaw, 'id' | 'value' | 'partnerId' | 'clientId'>;

export const useClientEmails = (
  params: {
    clientId?: string;
    limit?: number;
  },
  options?: QueryHookOptions<GetClientEmailQuery>,
) => {
  const { clientId, limit } = params;

  const { data, fetchMore, variables, ...rest } = useQuery<
    GetClientEmailQuery,
    QueryVariables
  >(GET_CLIENT_EMAILS_QUERY, {
    ...options,
    skip: !clientId || options?.skip,
    variables: {
      params: {
        filters: [
          {
            attribute: 'clientId',
            operator: '=',
            value: clientId!,
          },
        ],
        limit,
      },
    },
  });

  const clientEmails = useMemo(
    () => data?.getClientEmail?.records ?? [],
    [data?.getClientEmail],
  );

  const { loadMore, hasReachedEnd } = useLoadMore<GetClientEmailQuery>({
    offset: data?.getClientEmail?.records?.length ?? 0,
    fetchMore,
    params: variables?.params!,
  });

  const totalRecords = data?.getClientEmail.metadata?.totalRecords ?? 0;

  return {
    clientEmails,
    loadMore,
    totalRecords,
    hasMore: data?.getClientEmail.metadata?.totalRecords
      ? totalRecords > (data?.getClientEmail?.records?.length ?? 0)
      : !hasReachedEnd,
    ...rest,
  };
};

const UPDATE_CLIENT_EMAIL_MUTATION = gql`
  mutation UpdateClientEmail($conditions: [ConditionInput], $data: ClientEmailDataInput) {
    updateClientEmail(conditions: $conditions, data: $data) {
      ...ClientEmailFields
    }
  }
  ${CLIENT_EMAIL_FIELDS}
`;

const CREATE_CLIENT_EMAIL_MUTATION = gql`
  mutation CreateClientEmail($data: ClientEmailDataInput) {
    createClientEmail(data: $data) {
      ...ClientEmailFields
    }
  }
  ${CLIENT_EMAIL_FIELDS}
`;

const DELETE_CLIENT_EMAIL_MUTATION = gql`
  mutation DeleteClientEmail($conditions: [ConditionInput]) {
    deleteClientEmail(conditions: $conditions) {
      ...ClientEmailFields
    }
  }
  ${CLIENT_EMAIL_FIELDS}
`;

export const useClientEmail = () => {
  const { showErrorAlert } = useNotifications();

  const [updateClientEmailMutation] = useMutation<{
    updateClientEmail: ClientEmailRaw[];
  }>(UPDATE_CLIENT_EMAIL_MUTATION, {
    onError: (error) => {
      console.error(error);
      // TODO: Move to a generic error handler
      let message = 'Something went wrong updating the client email, please try again';
      if (error.message.includes('duplicate key value violates unique constraint')) {
        message = 'Unable to update email. Email is already being used by another client';
      }
      showErrorAlert(message);
    },
  });

  const updateClientEmail = useCallback(
    async (
      clientEmail: Partial<ClientEmailRaw> & {
        label?: string;
      },
    ) => {
      if (!clientEmail.id) {
        return;
      }

      const patch = removeNotDefinedProperties({
        value: clientEmail.value,
        labelId: clientEmail.labelId || undefined,
      });

      const { data } = await updateClientEmailMutation({
        variables: {
          conditions: [
            {
              attribute: 'clientEmailId',
              operator: '=',
              value: clientEmail.id,
            },
          ],
          data: patch,
        },
        optimisticResponse: {
          updateClientEmail: [
            {
              ...clientEmail,
              ...patch,
            } as ClientEmailRaw,
          ],
        },
      });

      return data?.updateClientEmail[0];
    },
    [updateClientEmailMutation],
  );

  const [createClientEmailMutation] = useMutation<{
    createClientEmail: ClientEmailRaw[];
  }>(CREATE_CLIENT_EMAIL_MUTATION, {
    onError: (error) => {
      console.error(error);
      // TODO: Move to a generic error handler
      let message = 'Something went wrong creating the client email, please try again';
      if (error.message.includes('duplicate key value violates unique constraint')) {
        message = 'Unable to create email. Email is already being used by another client';
      }
      showErrorAlert(message);
    },
  });

  const createClientEmail = useCallback(
    async (
      clientEmail: Partial<ClientEmailRaw> & {
        label?: string;
      },
    ) => {
      if (!clientEmail.value) {
        return;
      }

      const patch = removeNotDefinedProperties({
        value: clientEmail.value,
        labelId: clientEmail.labelId || undefined,
        partnerId: clientEmail.partnerId,
        clientId: clientEmail.clientId,
      });

      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const { data } = await createClientEmailMutation({
        variables: {
          data: patch,
        },
        optimisticResponse: {
          createClientEmail: [
            {
              ...clientEmail,
              ...patch,
              id: clientEmail.id || tempId,
              clientEmailId: clientEmail.id || tempId,
            } as ClientEmailRaw,
          ],
        },
        update: (cache, { data: response }) => {
          const created = response?.createClientEmail?.[0];
          if (!created) return;

          const id = cache.identify({
            __typename: 'Client',
            id: clientEmail.clientId,
          });

          cache.modify({
            id,
            fields: {
              emails(existingItems = [], { toReference, readField }) {
                const existingEmails = existingItems as Reference[];
                const id = clientEmail.id || created.id;

                const newEmail = {
                  __typename: 'ClientEmail',
                  id,
                  clientEmailId: id,
                  value: clientEmail.value || created.value,
                  labelId: clientEmail.labelId,
                };

                // Quick safety check - if the new email is already
                // present in the cache, we don't need to add it again.
                if (existingEmails.some((ref) => readField('id', ref) === created.id)) {
                  return existingEmails;
                }

                const reference = toReference(newEmail, true);

                if (reference) {
                  return [reference, ...existingEmails];
                }

                return [newEmail, ...existingEmails];
              },
            },
          });
        },
      });

      return data?.createClientEmail[0];
    },
    [createClientEmailMutation],
  );

  const [deleteClientEmailMutation] = useMutation<{
    deleteClientEmail: ClientEmailRaw[];
  }>(DELETE_CLIENT_EMAIL_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong deleting the client email, please try again');
    },
  });

  const deleteClientEmail = useCallback(
    async (clientEmails?: Partial<ClientEmailRaw> | Partial<ClientEmailRaw>[]) => {
      if (
        (Array.isArray(clientEmails) && !clientEmails.length) ||
        (!Array.isArray(clientEmails) && !clientEmails?.id)
      ) {
        return;
      }

      const ids = Array.isArray(clientEmails)
        ? clientEmails.filter((email) => !!email.id).map((email) => email.id)
        : [clientEmails.id];

      const { data } = await deleteClientEmailMutation({
        variables: {
          conditions: [
            {
              attribute: 'clientEmailId',
              operator: 'in',
              value: ids.join(','),
            },
          ],
        },
        optimisticResponse: {
          deleteClientEmail: Array.isArray(clientEmails)
            ? (clientEmails as ClientEmailRaw[])
            : [clientEmails as ClientEmailRaw],
        },
        update: (cache, { data: response }) => {
          const deleted = response?.deleteClientEmail;
          if (!deleted?.length) return;

          deleted.forEach((email) => {
            const id = cache.identify({
              __typename: 'Client',
              id: email.clientId,
            });

            cache.modify({
              id,
              fields: {
                emails(existingItems = [], { readField }) {
                  const existingEmails = existingItems as Reference[];
                  return existingEmails.filter(
                    (existingEmail) => readField('id', existingEmail) !== email.id,
                  );
                },
                primaryEmail(existingItem = {}, { readField }) {
                  const existingEmail = existingItem as Reference;
                  if (readField('id', existingEmail) === email.id) {
                    return null;
                  }

                  return existingEmail;
                },
              },
            });
          });
        },
      });

      return data?.deleteClientEmail;
    },
    [deleteClientEmailMutation],
  );

  return {
    deleteClientEmail,
    updateClientEmail,
    createClientEmail,
  };
};
